import * as Yup from 'yup';

import {
  FIELD_IS_NUMERIC_STRING,
  FIELD_IS_REQUIRED,
  toFieldMaximumValueErrorMessage,
  toFieldMinimumValueErrorMessage,
} from 'src/common/validation/errors';
import type { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

export type SpecificationIntervalPopupFormValuesItem = {
  minValue: number | null;
  maxValue: number | null;

  measureUnit: SpecificationMeasureUnit | null;
};

export type SpecificationIntervalPopupFormValues = {
  values: SpecificationIntervalPopupFormValuesItem[];
};

export const specificationIntervalPopupFormItemValidationSchema = (
  minIntValue: number | null,
  maxIntValue: number | null
): Yup.ObjectSchema<SpecificationIntervalPopupFormValuesItem, SpecificationIntervalPopupFormValuesItem> =>
  Yup.object<SpecificationIntervalPopupFormValuesItem>()
    .shape(
      {
        measureUnit: Yup.mixed<SpecificationMeasureUnit>().required(FIELD_IS_REQUIRED).default(null),

        minValue: Yup.number()
          .nullable()
          .test('minValue', 'Specify at least one value', (minValue, { parent: { maxValue } }) => {
            return minValue != null || maxValue != null;
          })
          .when('minValue', {
            is: (minValue: number | null) => minValue != null || minIntValue != null,
            then: (schema) =>
              schema.typeError(FIELD_IS_NUMERIC_STRING).when({
                is: () => minIntValue != null,
                then: (minSchema) =>
                  minSchema.min(
                    minIntValue as number,
                    toFieldMinimumValueErrorMessage('Min Value', minIntValue as number)
                  ),
                otherwise: (minSchema) =>
                  minSchema.when({
                    is: () => maxIntValue != null,
                    then: (maxSchema) =>
                      maxSchema.max(
                        maxIntValue as number,
                        toFieldMaximumValueErrorMessage('Max Value', maxIntValue as number)
                      ),
                    otherwise: (maxSchema) => maxSchema.required(FIELD_IS_REQUIRED),
                  }),
              }),
          })
          .default(null),
        maxValue: Yup.number()
          .nullable()
          .when('maxValue', {
            is: (maxValue: number | null) => maxValue != null || maxIntValue != null,
            then: (schema) =>
              schema.typeError(FIELD_IS_NUMERIC_STRING).when({
                is: () => minIntValue != null,
                then: (minSchema) =>
                  minSchema.min(
                    minIntValue as number,
                    toFieldMinimumValueErrorMessage('Min Value', minIntValue as number)
                  ),

                otherwise: (minSchema) =>
                  minSchema.when({
                    is: () => maxIntValue != null,
                    then: (maxSchema) =>
                      maxSchema.max(
                        maxIntValue as number,
                        toFieldMaximumValueErrorMessage('Max Value', maxIntValue as number)
                      ),
                    otherwise: (maxSchema) => maxSchema.required(FIELD_IS_REQUIRED),
                  }),
              }),
          })
          .default(null),
      },
      [
        ['minValue', 'minValue'],
        ['maxValue', 'maxValue'],
      ]
    )
    .required(FIELD_IS_REQUIRED);

export const specificationIntervalPopupFormValidationSchema = (
  minIntValue: number | null,
  maxIntValue: number | null
) =>
  Yup.object<SpecificationIntervalPopupFormValues>().shape({
    values: Yup.array()
      .of(specificationIntervalPopupFormItemValidationSchema(minIntValue, maxIntValue))
      .required(FIELD_IS_REQUIRED),
  });
